/**
 * Trunnion AI - Login Page
 */

import { useState, type FormEvent } from 'react';
import { Shield, Loader2, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ClassificationBanner } from '@/components/layout/ClassificationBanner';

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login({ username, password });
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
        const status = axiosErr.response?.status;
        const detail = axiosErr.response?.data?.detail;
        if (status === 401) {
          setError(detail || 'Invalid credentials. Please try again.');
        } else if (status === 403) {
          setError(detail || 'Account is disabled.');
        } else {
          setError(`Server error (${status}): ${detail || axiosErr.message || 'Unknown error'}`);
        }
      } else if (err instanceof Error) {
        setError(`Connection error: ${err.message}`);
      } else {
        setError('Unable to reach the server. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-trunnion-slate-950">
      <ClassificationBanner position="top" />

      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-trunnion-gold-500/10">
              <Shield className="h-8 w-8 text-trunnion-gold-500" />
            </div>
            <h1 className="text-3xl font-bold text-white">
              Trunnion <span className="text-trunnion-gold-500">AI</span>
            </h1>
            <p className="mt-2 text-sm text-trunnion-slate-400">
              DAF Agentic Workflow Platform
            </p>
          </div>

          {/* Login Form */}
          <form
            onSubmit={handleSubmit}
            className="rounded-xl border border-trunnion-slate-700 bg-trunnion-slate-800/50 p-6 shadow-xl"
          >
            <h2 className="mb-6 text-lg font-semibold text-white text-center">
              <Lock className="h-4 w-4 inline mr-2" />
              Secure Sign In
            </h2>

            <div className="space-y-4">
              <Input
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                autoComplete="username"
                required
              />
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="mt-6 w-full"
              loading={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="mt-4 text-center text-xs text-trunnion-slate-500">
            Authorized personnel only. All access is logged and monitored.
          </p>
        </div>
      </div>

      <ClassificationBanner position="bottom" />
    </div>
  );
}
